export type Language = 'en' | 'ka'

export const translations = {
  en: {
    header: {
      forCompanies: 'For Companies',
      forSeafarers: 'For Seafarers',
      offshoreServices: 'Offshore Services',
      sendCV: 'Send CV',
      menu: 'Menu',
    },
    seo: {
      title:
        'ELVORA Consulting — operational and technical support for marine and offshore projects',
      description:
        'Coordination, documentation and compliance support, analytics, and urgent requests for marine and offshore projects. For companies and seafarers.',
    },
    hero: {
      title:
        'Operational and technical support for marine and offshore projects',
      description:
        'We help shipowners, operators, and technical teams solve operational, technical, and organizational challenges — from single tasks to full project support',
      discussTask: 'Discuss Your Task',
      submitProfile: 'Submit Your Profile',
      tagline: 'Focused on long-term partnerships, not short-term placements',
    },
    services: {
      pill: 'About',
      title: 'How We Support Marine\n& Industrial Operations',
      desc1:
        'We support companies and operators by solving operational, technical, and organizational tasks.',
      desc2:
        'From documentation and coordination to complex, non-standard requests — ELVORA delivers clear and practical solutions',
      items: {
        projectSupport: {
          title: 'Project & Operational Support',
          desc: 'End-to-end assistance for ongoing operations and projects',
        },
        documentation: {
          title: 'Documentation & Compliance',
          desc: 'Structured management of documents, requirements, and approvals',
        },
        technical: {
          title: 'Technical & Analytical Support',
          desc: 'Analysis, reporting, and structured decision support',
        },
        coordination: {
          title: 'Coordination & Process Management',
          desc: 'Clear communication and control across all involved parties',
        },
        nonStandard: {
          title: 'Non-Standard Requests',
          desc: 'Fast response to urgent or complex tasks that require attention',
        },
      },
    },
    clients: {
      pill: 'Services',
      title: 'How We Support\nOur Clients',
      desc1:
        'We handle operational, technical, and organizational tasks for marine and industrial projects.',
      desc2:
        'From documentation and coordination to complex, non-standard requests — we focus on solutions, not labels.',
      tabCompanies: 'For Companies',
      tabSpecialists: 'For Specialists',
      learnMoreSpecialists: 'Learn More About Specialists',
      learnMoreCompanies: 'Learn More About Companies',
      specialistItems: [
        {
          id: '01',
          title: 'Profile Submission & Review',
          desc: 'We review professional profiles and documentation to match specialists with relevant tasks and projects',
        },
        {
          id: '02',
          title: 'Documentation Guidance',
          desc: 'Support with understanding requirements, documentation structure, and compliance expectations',
        },
        {
          id: '03',
          title: 'Project Coordination',
          desc: 'Clear communication throughout the process of task or project involvement',
        },
        {
          id: '04',
          title: 'Professional Support',
          desc: 'Guidance and assistance during cooperation with companies and project teams',
        },
        {
          id: '05',
          title: 'Long-Term Opportunities',
          desc: 'Building structured long-term professional cooperation based on experience and reliability',
        },
      ],
      companyItems: [
        {
          id: '01',
          title: 'Project & Operational Support',
          desc: 'We support ongoing operations and projects by taking responsibility for specific tasks, coordination, and execution',
        },
        {
          id: '02',
          title: 'Documentation & Compliance',
          desc: 'Structured handling of documentation, requirements, and approvals according to project and regulatory needs',
        },
        {
          id: '03',
          title: 'Coordination & Process Management',
          desc: 'Clear communication and control between departments, contractors, and involved parties',
        },
        {
          id: '04',
          title: 'Technical & Analytical Support',
          desc: 'Analysis, reporting, and structured information to support operational and management decisions',
        },
        {
          id: '05',
          title: 'Non-Standard & Urgent Tasks',
          desc: 'Fast response to complex or time-sensitive requests that require experience and attention',
        },
      ],
    },
    faq: {
      pill: 'FAQ',
      title: 'Have a Question?',
      desc: 'For quick questions or clarifications, you can contact us via messenger.',
      items: [
        {
          id: '01',
          q: 'What types of tasks do you handle?',
          a: 'We work with operational, technical, organizational, and documentation-related tasks across marine, energy, and industrial projects.',
        },
        {
          id: '02',
          q: 'Who can contact ELVORA?',
          a: 'Shipowners, technical managers, operators, and industrial project leads can contact us for support with their operational challenges.',
        },
        {
          id: '03',
          q: 'Do you only work with offshore projects?',
          a: 'No, we work with both offshore and onshore projects across marine, energy, and industrial sectors.',
        },
        {
          id: '04',
          q: 'How do you start working on a request?',
          a: 'We begin with a brief consultation to understand your needs, then provide a clear proposal with timeline and deliverables.',
        },
        {
          id: '05',
          q: 'Is the information confidential?',
          a: 'Yes, all information is treated with strict confidentiality. We adhere to NDA protocols and data protection standards.',
        },
      ],
    },
    jobs: {
      pill: 'Vacancies',
      sendResume: 'Send resume',
      items: [
        {
          title: 'Marine Engineer',
          type: 'Oil Tanker / VLCC',
          desc: 'Experienced marine engineer needed for VLCC operations. Responsible for main engine maintenance, troubleshooting, and ensuring compliance with safety regulations.',
        },
        {
          title: 'Chief Officer',
          type: 'Container Vessel',
          desc: 'Seeking qualified Chief Officer for container vessel operations. Responsibilities include cargo operations, navigation, safety management, and crew supervision.',
        },
        {
          title: 'Electrical Engineer',
          type: 'Offshore Platform',
          desc: 'Electrical engineer position available for offshore platform. Maintain and repair electrical systems, power generation equipment, and control systems.',
        },
        {
          title: 'Deck Cadet',
          type: 'Bulk Carrier',
          desc: 'Entry-level position for deck cadet on bulk carrier. Training opportunity for maritime academy graduates.',
        },
        {
          title: 'Second Engineer',
          type: 'LNG Carrier',
          desc: 'Second Engineer required for LNG carrier operations. Manage engine room operations, maintenance schedules, and technical documentation.',
        },
        {
          title: 'Bosun',
          type: 'Cruise Ship',
          desc: 'Experienced Bosun needed for luxury cruise operations. Lead deck crew, coordinate maintenance activities.',
        },
        {
          title: 'Marine Superintendent',
          type: 'Fleet Management',
          desc: 'Shore-based Marine Superintendent position. Oversee technical operations of vessel fleet, conduct inspections.',
        },
        {
          title: 'QHSE Manager',
          type: 'Offshore Installation',
          desc: 'Quality, Health, Safety & Environment Manager for offshore operations. Develop and implement QHSE policies.',
        },
        {
          title: 'AB Seaman',
          type: 'General Cargo',
          desc: 'Able Bodied Seaman position on general cargo vessel. Perform deck maintenance, cargo operations, and watchkeeping duties.',
        },
        {
          title: 'Chief Engineer',
          type: 'Chemical Tanker',
          desc: 'Chief Engineer position for chemical tanker operations. Overall responsibility for engine room and maintenance planning.',
        },
        {
          title: 'Radio Officer',
          type: 'Research Vessel',
          desc: 'Radio Officer needed for scientific research vessel. Maintain communication systems and support research operations.',
        },
        {
          title: 'Pumpman',
          type: 'Product Tanker',
          desc: 'Experienced Pumpman for product tanker operations. Responsible for cargo pumping systems and tank cleaning.',
        },
        {
          title: 'DPO',
          type: 'Offshore Support Vessel',
          desc: 'Dynamic Positioning Operator for OSV operations. Operate DP systems during offshore operations.',
        },
        {
          title: 'Crane Operator',
          type: 'Heavy Lift Vessel',
          desc: 'Certified crane operator for heavy lift vessel. Operate deck cranes for cargo operations.',
        },
        {
          title: 'Catering Manager',
          type: 'Passenger Ferry',
          desc: 'Catering Manager for passenger ferry operations. Manage galley operations, food service, and inventory.',
        },
      ],
    },
    contact: {
      title: 'Contact us',
      desc: 'Tell us about your task, challenge, or project. We will review the request and get back to you with a clear and practical solution',
      labels: {
        company: 'Company name',
        person: 'Contact person',
        email: 'Email address',
        phone: 'Phone / WhatsApp',
        task: 'Describe your task or request',
        project: 'Project / Vessel type',
        files: 'Attach files',
        dragDrop: 'Drag and drop files here, or click to browse',
        fileTypes: 'PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, GIF, WebP (Max 48 MB)',
        phoneHint: 'Include country code (e.g., +1, +44, +380)',
      },
      button: 'Submit Request',
      disclaimer:
        'All information is treated confidentially. We do not share your data with third parties',
    },
    footer: {
      discussTask: 'Discuss Your Task',
      sendProfile: 'Send Your Profile',
      privacy: 'Privacy Policy',
      terms: 'Terms of Use',
    },
    specialists: {
      hero: {
        title: 'Maritime Recruitment Support for Seafarers',
        desc: 'This section is dedicated to seafarers seeking professional recruitment support, profile review, and access to career opportunities with shipowners and operators in the marine and offshore sectors.',
        submitCV: 'Submit Seafarer CV',
        viewOpportunities: 'View Opportunities',
        tagline1:
          'Connecting with verified shipowners and operators through professional recruitment support.',
        tagline2:
          'Submit your profile for structured review, documentation guidance, and opportunity consideration.',
      },
      seo: {
        title: 'ELVORA Consulting — Maritime Recruitment Support for Seafarers',
        description:
          'Professional recruitment support for seafarers: profile review, documentation guidance, and access to current opportunities with shipowners and operators in marine and offshore sectors.',
      },
      benefits: {
        pill: 'Support',
        title: 'Professional Recruitment\nSupport for Seafarers',
        desc1:
          'We provide structured recruitment support, profile screening, and documentation guidance for seafarers seeking employment with verified maritime and offshore operators.',
        desc2:
          'Submit your seafarer profile for structured review, documentation guidance, and consideration for marine and offshore opportunities.',
        items: {
          profile: {
            title: 'Profile Review & Screening',
            desc: 'Seafarer profiles are reviewed to assess rank, vessel experience, and documentation readiness before being considered for current or future opportunities.',
          },
          docs: {
            title: 'Document & Compliance Guidance',
            desc: 'Guidance on commonly required certificates, endorsements, and documentation requested by shipowners and offshore operators.',
          },
          matching: {
            title: 'Recruitment Matching Support',
            desc: 'Professional support in aligning seafarer profiles with recruitment requests based on experience, availability, and project requirements.',
          },
          access: {
            title: 'Access to Verified Opportunities',
            desc: 'Consideration for marine and offshore opportunities with shipowners and operators, subject to profile review and client requirements.',
          },
        },
      },
      steps: {
        pill: 'Process',
        title: 'How It Works',
        desc: 'Your path from application to placement',
        items: [
          {
            title: 'Profile Submission',
            desc: 'Submit your CV, rank, vessel experience, and availability for professional review and consideration.',
          },
          {
            title: 'Review & Screening',
            desc: 'We verify your documents and assess suitability for current and upcoming recruitment requests.',
          },
          {
            title: 'Recruitment Consideration',
            desc: 'Qualified profiles are matched to shipowner and operator requirements based on experience.',
          },
          {
            title: 'Direct Client Interaction',
            desc: 'If shortlisted, you communicate directly with the hiring company to finalize employment terms.',
          },
        ],
      },
      requirements: {
        pill: 'Documents',
        title: 'Required Documents for Seafarer Recruitment',
        desc: 'Standard documents commonly required during the seafarer recruitment and profile review process',
        items: [
          "Seafarer's Book",
          'Passport (valid)',
          'Certificate of Competency (CoC)',
          'STCW Certificates',
          'Medical Certificate (ENG1 / equivalent)',
          'BOSIET / Offshore Safety Certificates (if applicable)',
          'DP Certificate (for DP positions, if applicable)',
          'CV / Sea Service Records',
        ],
        note: 'The exact list of required documents may vary depending on the vessel type, project requirements, flag state, and client policies.',
      },
      faq: {
        pill: 'FAQ',
        title: 'Frequently Asked Questions',
        items: [
          {
            q: 'Do you guarantee employment after submitting a CV?',
            a: 'No. Submitting a profile does not guarantee employment. All profiles are reviewed individually and may be considered for recruitment requests based on experience, availability, and client requirements.',
          },
          {
            q: 'Do you act as a crewing or manning agency?',
            a: 'No. Elvora provides recruitment support and advisory services. Employment decisions and contracts are handled directly between seafarers and hiring companies.',
          },
          {
            q: 'Do you charge seafarers any fees?',
            a: 'We do not charge seafarers for submitting profiles. Some optional services, such as documentation guidance or career consulting, may be offered separately if requested.',
          },
          {
            q: 'What types of vessels and projects do you work with?',
            a: 'We support recruitment requests across marine and offshore sectors, including merchant vessels, offshore support vessels, and project-based operations.',
          },
          {
            q: 'How long does the review process take?',
            a: 'Profile reviews depend on current recruitment activity and client requests. Only shortlisted candidates are contacted.',
          },
          {
            q: 'How will I be contacted if my profile is suitable?',
            a: 'Shortlisted candidates are contacted directly using the contact details provided during profile submission.',
          },
        ],
      },
      form: {
        pill: 'Application',
        title: 'Submit Your Seafarer Profile',
        desc1:
          'Submit your professional profile for review as part of the recruitment support process.',
        desc2: 'Submission does not guarantee employment.',
        labels: {
          firstName: 'First Name',
          lastName: 'Last Name',
          rank: 'Rank / Position',
          selectRank: 'Select rank/position',
          specify: 'Please specify',
          enterPosition: 'Enter your position',
          email: 'Email',
          phone: 'Phone / WhatsApp',
          phoneHint: 'Include country code (e.g., +1, +44, +380)',
          cv: 'CV Upload (PDF, DOC, or Image)',
          dragDrop: 'Drag and drop your CV here, or click to browse',
          fileTypes: 'PDF, DOC, DOCX, PNG, JPG, GIF, WebP (Max 48 MB)',
          vesselType: 'Vessel Type Experience',
          selectVessel: 'Select vessel type',
          yearsExp: 'Years of Experience',
          availability: 'Availability Date',
          availabilityHint: 'Select your earliest availability date',
          consent:
            'I confirm that the information provided is accurate and agree to the processing of my personal data for recruitment support purposes.',
          submit: 'Submit Profile',
          disclaimer:
            'All information is treated confidentially. Only shortlisted candidates are contacted.',
        },
        ranks: {
          deck: 'Deck Department',
          engine: 'Engine Department',
          other: 'Other',
          options: {
            master: 'Master',
            chiefMate: 'Chief Mate',
            secondMate: 'Second Mate',
            thirdMate: 'Third Mate',
            bosun: 'Bosun',
            ab: 'Able Seaman (AB)',
            os: 'Ordinary Seaman (OS)',
            chiefEng: 'Chief Engineer',
            secondEng: 'Second Engineer',
            thirdEng: 'Third Engineer',
            fourthEng: 'Fourth Engineer',
            eto: 'ETO',
            asstEto: 'Assistant ETO',
            motorman: 'Motorman / Oiler',
            wiper: 'Wiper',
            dpo: 'DPO',
            sdpo: 'SDPO',
            craneOp: 'Crane Operator',
            rigger: 'Rigger',
            electrician: 'Electrician',
            mechanic: 'Mechanic',
            other: 'Other (please specify)',
          },
        },
        vessels: {
          tanker: 'Tanker',
          bulk: 'Bulk Carrier',
          container: 'Container Vessel',
          offshore: 'Offshore Support Vessel',
          cruise: 'Cruise Ship',
          other: 'Other',
        },
      },
      contact: {
        pill: 'Contact',
        title: 'Need Help?',
        desc: 'Contact us via messenger or email for quick assistance',
      },
    },
    companies: {
      hero: {
        title: 'Reliable crew supply for vessels and offshore projects',
        desc1:
          'Professional recruitment, screening, and crew management services for shipowners and operators.',
        desc2: 'From single positions to full crew solutions.',
        requestCrew: 'Request Crew',
        discussTask: 'Discuss Your Task',
      },
      seo: {
        title:
          'ELVORA Consulting — Reliable crew supply for vessels & offshore projects',
        description:
          'Professional recruitment, screening, and crew management for shipowners and operators. From single positions to full crews for marine and offshore projects.',
      },
      services: {
        pill: 'Services',
        title: 'Services for Shipowners\n& Operators',
        desc1:
          'Comprehensive crew recruitment and management solutions for maritime and offshore operations.',
        desc2:
          'From candidate selection to full compliance and crew logistics.',
        items: {
          recruitment: {
            title: 'Crew Recruitment',
            desc: 'Full-cycle recruitment for all ranks and vessel types — offshore and merchant fleet.',
          },
          screening: {
            title: 'Screening & Verification',
            desc: 'Document verification, background checks, experience validation, and compliance screening.',
          },
          compliance: {
            title: 'Compliance Management',
            desc: 'STCW, MLC, flag state and client-specific requirements fully covered.',
          },
          coordination: {
            title: 'Crew Coordination',
            desc: 'Crew changes, travel arrangements, mobilization, and replacement planning.',
          },
        },
      },
      steps: {
        pill: 'Process',
        title: 'How We Work',
        desc: 'Clear and transparent crew recruitment process — from request to deployment',
        items: [
          {
            title: 'Request',
            desc: 'Submit your crew requirements and vessel or project details',
          },
          {
            title: 'Screening',
            desc: 'Candidate selection, document verification, and compliance checks',
          },
          {
            title: 'Matching & Interview',
            desc: 'Shortlisting qualified candidates and arranging interviews',
          },
          {
            title: 'Deployment',
            desc: 'Contract signing, crew mobilization, and ongoing coordination',
          },
        ],
      },
      compliance: {
        pill: 'Compliance',
        title: 'Compliance & Confidentiality',
        desc: 'We operate in full compliance with international maritime standards and ensure strict data protection',
        items: {
          nda: {
            title: 'NDA & Data Protection',
            desc: 'All client and crew information is handled under strict confidentiality and non-disclosure principles.',
          },
          verification: {
            title: 'Document Verification',
            desc: 'Thorough verification of certificates, licenses, medicals, and experience before submission.',
          },
          stcw: {
            title: 'STCW & MLC Compliance',
            desc: 'Full compliance with STCW, MLC 2006, flag state, and client-specific requirements.',
          },
        },
      },
      faq: {
        pill: 'FAQ',
        title: 'Frequently Asked Questions',
        items: [
          {
            q: 'Do you act as an employer or crewing agency?',
            a: 'No. Elvora provides recruitment and operational support services. Employment contracts are signed directly between the crew and the hiring company.',
          },
          {
            q: 'What types of vessels and projects do you support?',
            a: 'We support merchant vessels and offshore projects, including tankers, offshore support vessels, and project-based marine operations.',
          },
          {
            q: 'Do you provide single crew members or full crews?',
            a: 'We support both single-position requests and full or partial crew solutions, depending on project scope and client requirements.',
          },
          {
            q: 'How do you ensure candidate quality and compliance?',
            a: 'All candidates undergo document verification, experience review, and compliance checks in line with STCW, MLC, flag state, and client-specific standards.',
          },
          {
            q: 'How quickly can crew be mobilized?',
            a: 'Mobilization timelines depend on vessel type, position, availability, and documentation status. Timelines are discussed individually for each request.',
          },
          {
            q: 'Do you work worldwide?',
            a: 'Yes. We support international projects and cooperate with shipowners and operators across multiple regions.',
          },
        ],
      },
      form: {
        pill: 'Request',
        title: 'Request Crew or Discuss Your Project',
        desc1: 'Submit your crew requirements or project details.',
        desc2: 'We review each request individually and respond accordingly.',
        sections: {
          company: 'Company Information',
          details: 'Request Details',
          timeline: 'Timeline',
        },
        labels: {
          companyName: 'Company Name',
          contactPerson: 'Contact Person',
          email: 'Email',
          phone: 'Phone / WhatsApp',
          vesselType: 'Vessel / Project Type',
          selectVessel: 'Select vessel/project type',
          positions: 'Positions Required',
          selectPositions: 'Select positions',
          crewNumber: 'Number of Crew',
          location: 'Location / Region',
          selectLocation: 'Select region (optional)',
          additional: 'Additional Requirements',
          additionalPlaceholder:
            'Specific certifications, experience requirements, project details...',
          startDate: 'Desired Start Date',
          duration: 'Contract Duration',
          selectDuration: 'Select duration (optional)',
          consent:
            'I confirm that the information provided is accurate and agree to the processing of data for recruitment and operational support purposes.',
          submit: 'Submit Request',
          disclaimer:
            'All information is treated confidentially under NDA. We respond within 24 hours.',
        },
        vesselTypes: [
          'Merchant Vessel',
          'Tanker',
          'Offshore Support Vessel (OSV)',
          'Jack-Up / Offshore Unit',
          'Project-Based Operation',
          'Other',
        ],
        regions: [
          'Worldwide',
          'Europe',
          'Middle East',
          'Africa',
          'Asia-Pacific',
          'Other',
        ],
        durations: {
          m1: '1 month',
          m2: '2 months',
          m3: '3 months',
          m4: '4 months',
          m6: '6 months',
          m9: '9 months',
          m12: '12 months',
          ongoing: 'Ongoing / Permanent',
          project: 'Project-based',
        },
      },
      contact: {
        pill: 'Contact',
        title: 'Get in Touch',
        desc: 'Contact us for crew requests, consultations, or partnership opportunities',
      },
    },
    formStatus: {
      sending: 'Sending...',
      sent: 'Sent ✅',
      error: 'Error, please try again',
    },
    floating: {
      submitCV: 'Submit CV',
      consultation: 'Consultation',
      consultTitle: 'Get in Touch',
      consultDesc: 'Contact us directly or send a message',
      orSendMessage: 'or send us a message',
      labelName: 'Your Name',
      labelEmail: 'Email',
      labelPhone: 'Phone / WhatsApp',
      labelComment: 'Comment',
      commentPlaceholder: 'How can we help you?',
      submitBtn: 'Send Message',
      submitting: 'Sending...',
      success: 'Message sent!',
      successDesc: 'We will contact you soon.',
      error: 'Something went wrong. Please try again.',
      disclaimer: 'All information is treated confidentially.',
    },
  },
  ka: {
    header: {
      forCompanies: 'კომპანიებისთვის',
      forSeafarers: 'მეზღვაურებისთვის',
      offshoreServices: 'ოფშორული სერვისები',
      sendCV: 'CV-ს გაგზავნა',
      menu: 'მენიუ',
    },
    seo: {
      title:
        'ELVORA Consulting — საზღვაო და ოფშორული პროექტების ოპერაციული და ტექნიკური მხარდაჭერა',
      description:
        'ვუზრუნველყოფთ სამუშაოების კოორდინაციას, დოკუმენტაციას და მოთხოვნებთან შესაბამისობის (compliance) მხარდაჭერას, ანალიტიკას და გადაუდებელ მოთხოვნებზე რეაგირებას საზღვაო და ოფშორული პროექტებისთვის. კომპანიებისთვის და მეზღვაურებისთვის.',
    },
    hero: {
      title:
        'საზღვაო და ოფშორული პროექტების ოპერაციული და ტექნიკური მხარდაჭერა',
      description:
        'ჩვენ ვეხმარებით გემთმფლობელებს, ოპერატორებს და ტექნიკურ გუნდებს ოპერაციული, ტექნიკური და ორგანიზაციული გამოწვევების გადაჭრაში — ერთჯერადი დავალებებიდან სრულ საპროექტო მხარდაჭერამდე',
      discussTask: 'დავალების განხილვა',
      submitProfile: 'პროფილის გაგზავნა',
      tagline:
        'ორიენტირებული გრძელვადიან პარტნიორობაზე და არა მოკლევადიან დასაქმებაზე',
    },
    services: {
      pill: 'ჩვენს შესახებ',
      title: 'როგორ ვუჭერთ მხარს საზღვაო\nდა ინდუსტრიულ ოპერაციებს',
      desc1:
        'ჩვენ მხარს ვუჭერთ კომპანიებსა და ოპერატორებს ოპერაციული, ტექნიკური და ორგანიზაციული ამოცანების გადაჭრაში.',
      desc2:
        'დოკუმენტაციიდან და კოორდინაციიდან რთულ, არასტანდარტულ მოთხოვნებამდე — ELVORA გთავაზობთ მკაფიო და პრაქტიკულ გადაწყვეტილებებს',
      items: {
        projectSupport: {
          title: 'საპროექტო და ოპერაციული მხარდაჭერა',
          desc: 'კომპლექსური დახმარება მიმდინარე ოპერაციებისა და პროექტებისთვის',
        },
        documentation: {
          title: 'დოკუმენტაცია და შესაბამისობა',
          desc: 'დოკუმენტების, მოთხოვნებისა და დამტკიცებების სტრუქტურირებული მართვა',
        },
        technical: {
          title: 'ტექნიკური და ანალიტიკური მხარდაჭერა',
          desc: 'ანალიზი, ანგარიშგება და გადაწყვეტილების მიღების სტრუქტურირებული მხარდაჭერა',
        },
        coordination: {
          title: 'კოორდინაცია და პროცესების მართვა',
          desc: 'მკაფიო კომუნიკაცია და კონტროლი ყველა ჩართულ მხარეს შორის',
        },
        nonStandard: {
          title: 'არასტანდარტული მოთხოვნები',
          desc: 'სწრაფი რეაგირება სასწრაფო ან რთულ ამოცანებზე, რომლებიც ყურადღებას მოითხოვს',
        },
      },
    },
    clients: {
      pill: 'სერვისები',
      title: 'როგორ ვუჭერთ მხარს\nჩვენს კლიენტებს',
      desc1:
        'ჩვენ ვასრულებთ ოპერაციულ, ტექნიკურ და ორგანიზაციულ ამოცანებს საზღვაო და ინდუსტრიული პროექტებისთვის.',
      desc2:
        'დოკუმენტაციიდან და კოორდინაციიდან რთულ, არასტანდარტულ მოთხოვნებამდე — ჩვენ ვფოკუსირდებით გადაწყვეტილებებზე.',
      tabCompanies: 'კომპანიებისთვის',
      tabSpecialists: 'სპეციალისტებისთვის',
      learnMoreSpecialists: 'გაიგეთ მეტი სპეციალისტების შესახებ',
      learnMoreCompanies: 'გაიგეთ მეტი კომპანიების შესახებ',
      specialistItems: [
        {
          id: '01',
          title: 'პროფილის წარდგენა და განხილვა',
          desc: 'ჩვენ განვიხილავთ პროფესიულ პროფილებს და დოკუმენტაციას სპეციალისტების შესაბამის ამოცანებსა და პროექტებთან დასაკავშირებლად',
        },
        {
          id: '02',
          title: 'კონსულტაცია დოკუმენტაციაზე',
          desc: 'მხარდაჭერა მოთხოვნების, დოკუმენტაციის სტრუქტურისა და შესაბამისობის მოლოდინების გაგებაში',
        },
        {
          id: '03',
          title: 'პროექტის კოორდინაცია',
          desc: 'მკაფიო კომუნიკაცია დავალებაში ან პროექტში ჩართულობის მთელი პროცესის განმავლობაში',
        },
        {
          id: '04',
          title: 'პროფესიული მხარდაჭერა',
          desc: 'ხელმძღვანელობა და დახმარება კომპანიებთან და საპროექტო გუნდებთან თანამშრომლობისას',
        },
        {
          id: '05',
          title: 'გრძელვადიანი შესაძლებლობები',
          desc: 'სტრუქტურირებული გრძელვადიანი პროფესიული თანამშრომლობის აგება გამოცდილებასა და საიმედოობაზე დაყრდნობით',
        },
      ],
      companyItems: [
        {
          id: '01',
          title: 'საპროექტო და ოპერაციული მხარდაჭერა',
          desc: 'ჩვენ მხარს ვუჭერთ მიმდინარე ოპერაციებსა და პროექტებს კონკრეტულ ამოცანებზე, კოორდინაციასა და შესრულებაზე პასუხისმგებლობით',
        },
        {
          id: '02',
          title: 'დოკუმენტაცია და შესაბამისობა',
          desc: 'დოკუმენტაციის, მოთხოვნებისა და დამტკიცებების სტრუქტურირებული დამუშავება პროექტისა და რეგულაციების შესაბამისად',
        },
        {
          id: '03',
          title: 'კოორდინაცია და პროცესების მართვა',
          desc: 'მკაფიო კომუნიკაცია და კონტროლი დეპარტამენტებს, კონტრაქტორებსა და ჩართულ მხარეებს შორის',
        },
        {
          id: '04',
          title: 'ტექნიკური და ანალიტიკური მხარდაჭერა',
          desc: 'ანალიზი, ანგარიშგება და სტრუქტურირებული ინფორმაცია ოპერაციული და მმართველობითი გადაწყვეტილების მხარდასაჭერად',
        },
        {
          id: '05',
          title: 'არასტანდარტული და სასწრაფო ამოცანები',
          desc: 'სწრაფი რეაგირება რთულ ან დროში შეზღუდულ მოთხოვნებზე, რომლებიც გამოცდილებასა და ყურადღებას მოითხოვს',
        },
      ],
    },
    faq: {
      pill: 'FAQ',
      title: 'გაქვთ კითხვები?',
      desc: 'სწრაფი კითხვებისთვის ან დაზუსტებისთვის შეგიძლიათ დაგვიკავშირდეთ მესენჯერის საშუალებით.',
      items: [
        {
          id: '01',
          q: 'რა ტიპის ამოცანებს ასრულებთ?',
          a: 'ჩვენ ვმუშაობთ ოპერაციულ, ტექნიკურ, ორგანიზაციულ და დოკუმენტაციასთან დაკავშირებულ ამოცანებზე საზღვაო, ენერგეტიკულ და ინდუსტრიულ პროექტებში.',
        },
        {
          id: '02',
          q: 'ვის შეუძლია დაუკავშირდეს ELVORA-ს?',
          a: 'გემთმფლობელებს, ტექნიკურ მენეჯერებს, ოპერატორებს და ინდუსტრიული პროექტების ხელმძღვანელებს შეუძლიათ დაგვიკავშირდნენ ოპერაციული გამოწვევების მხარდაჭერისთვის.',
        },
        {
          id: '03',
          q: 'მხოლოდ ოფშორულ პროექტებზე მუშაობთ?',
          a: 'არა, ჩვენ ვმუშაობთ როგორც ოფშორულ, ისე სანაპირო პროექტებზე საზღვაო, ენერგეტიკულ და ინდუსტრიულ სექტორებში.',
        },
        {
          id: '04',
          q: 'როგორ იწყებთ მოთხოვნაზე მუშაობას?',
          a: 'ჩვენ ვიწყებთ მოკლე კონსულტაციით თქვენი საჭიროებების გასაგებად, რის შემდეგაც გთავაზობთ მკაფიო წინადადებას ვადებითა და შედეგებით.',
        },
        {
          id: '05',
          q: 'არის თუ არა ინფორმაცია კონფიდენციალური?',
          a: 'დიახ, ყველა ინფორმაცია მუშავდება მკაცრი კონფიდენციალურობით. ჩვენ ვიცავთ NDA პროტოკოლებსა და მონაცემთა დაცვის სტანდარტებს.',
        },
      ],
    },
    jobs: {
      pill: 'ვაკანსიები',
      sendResume: 'რეზიუმეს გაგზავნა',
      items: [
        {
          title: 'Marine Engineer',
          type: 'Oil Tanker / VLCC',
          desc: 'გამოცდილი საზღვაო ინჟინერი VLCC ოპერაციებისთვის. პასუხისმგებლობა მთავარი ძრავის ტექნიკურ მომსახურებაზე, გაუმართაობის აღმოფხვრასა და უსაფრთხოების წესების დაცვაზე.',
        },
        {
          title: 'Chief Officer',
          type: 'Container Vessel',
          desc: 'ვეძებთ კვალიფიციურ უფროს თანაშემწეს საკონტეინერო გემისთვის. მოვალეობებში შედის ტვირთის ოპერაციები, ნავიგაცია, უსაფრთხოების მართვა და ეკიპაჟის ზედამხედველობა.',
        },
        {
          title: 'Electrical Engineer',
          type: 'Offshore Platform',
          desc: 'ელექტროინჟინრის პოზიცია ოფშორულ პლატფორმაზე. ელექტრო სისტემების, ელექტროენერგიის გამომუშავების აღჭურვილობისა და მართვის სისტემების მოვლა და შეკეთება.',
        },
        {
          title: 'Deck Cadet',
          type: 'Bulk Carrier',
          desc: 'საწყისი პოზიცია გემბანის კადეტისთვის ბალკერზე. ტრენინგის შესაძლებლობა საზღვაო აკადემიის კურსდამთავრებულებისთვის.',
        },
        {
          title: 'Second Engineer',
          type: 'LNG Carrier',
          desc: 'საჭიროა მეორე მექანიკოსი LNG გადამზიდისთვის. სამანქანო განყოფილების ოპერაციების, ტექნიკური მომსახურების გრაფიკებისა და ტექნიკური დოკუმენტაციის მართვა.',
        },
        {
          title: 'Bosun',
          type: 'Cruise Ship',
          desc: 'გამოცდილი ბოცმანი ლუქს კლასის საკრუიზო ლაინერისთვის. გემბანის ეკიპაჟის ხელმძღვანელობა, ტექნიკური მომსახურების სამუშაოების კოორდინაცია.',
        },
        {
          title: 'Marine Superintendent',
          type: 'Fleet Management',
          desc: 'საზღვაო სუპერინტენდანტის სანაპირო პოზიცია. ფლოტის ტექნიკური ოპერაციების ზედამხედველობა, ინსპექტირების ჩატარება.',
        },
        {
          title: 'QHSE Manager',
          type: 'Offshore Installation',
          desc: 'ხარისხის, ჯანმრთელობის, უსაფრთხოებისა და გარემოს დაცვის მენეჯერი ოფშორული ოპერაციებისთვის. QHSE პოლიტიკის შემუშავება და დანერგვა.',
        },
        {
          title: 'AB Seaman',
          type: 'General Cargo',
          desc: 'პირველი კლასის მატროსის პოზიცია გენერალური ტვირთების გემზე. გემბანის ტექნიკური მომსახურება, სატვირთო ოპერაციები და ვახტის დგომა.',
        },
        {
          title: 'Chief Engineer',
          type: 'Chemical Tanker',
          desc: 'უფროსი მექანიკოსის პოზიცია ქიმიურ ტანკერზე. საერთო პასუხისმგებლობა სამანქანო განყოფილებასა და ტექნიკური მომსახურების დაგეგმვაზე.',
        },
        {
          title: 'Radio Officer',
          type: 'Research Vessel',
          desc: 'საჭიროა რადიოოპერატორი სამეცნიერო-კვლევითი გემისთვის. საკომუნიკაციო სისტემების მომსახურება და კვლევითი ოპერაციების მხარდაჭერა.',
        },
        {
          title: 'Pumpman',
          type: 'Product Tanker',
          desc: 'გამოცდილი პამპმენი პროდუქტმზიდისთვის. პასუხისმგებლობა სატვირთო სატუმბ სისტემებსა და ტანკების რეცხვაზე.',
        },
        {
          title: 'DPO',
          type: 'Offshore Support Vessel',
          desc: 'დინამიური პოზიციონირების ოპერატორი OSV-სთვის. DP სისტემების მართვა ოფშორული ოპერაციების დროს.',
        },
        {
          title: 'Crane Operator',
          type: 'Heavy Lift Vessel',
          desc: 'სერტიფიცირებული ამწის ოპერატორი მძიმე ტვირთმზიდი გემისთვის. გემბანის ამწეების მართვა სატვირთო ოპერაციებისთვის.',
        },
        {
          title: 'Catering Manager',
          type: 'Passenger Ferry',
          desc: 'კვების მენეჯერი სამგზავრო ბორანისთვის. სამზარეულოს ოპერაციების, კვების სერვისისა და ინვენტარის მართვა.',
        },
      ],
    },
    contact: {
      title: 'დაგვიკავშირდით',
      desc: 'გვიამბეთ თქვენი ამოცანის, გამოწვევის ან პროექტის შესახებ. ჩვენ განვიხილავთ მოთხოვნას და დაგიბრუნდებით მკაფიო და პრაქტიკული გადაწყვეტილებით',
      labels: {
        company: 'კომპანიის სახელი',
        person: 'საკონტაქტო პირი',
        email: 'ელ. ფოსტა',
        phone: 'ტელეფონი / WhatsApp',
        task: 'აღწერეთ თქვენი ამოცანა ან მოთხოვნა',
        project: 'პროექტის / გემის ტიპი',
        files: 'ფაილების მიმაგრება',
        dragDrop: 'გადმოიტანეთ ფაილები აქ ან დააჭირეთ ასარჩევად',
        fileTypes:
          'PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, GIF, WebP (მაქს. 48 MB)',
        phoneHint: 'მიუთითეთ ქვეყნის კოდი (მაგ., +1, +44, +995)',
      },
      button: 'მოთხოვნის გაგზავნა',
      disclaimer:
        'მთელი ინფორმაცია კონფიდენციალურია. ჩვენ არ გადავცემთ თქვენს მონაცემებს მესამე მხარეებს',
    },
    footer: {
      discussTask: 'დავალების განხილვა',
      sendProfile: 'პროფილის გაგზავნა',
      privacy: 'კონფიდენციალურობის პოლიტიკა',
      terms: 'გამოყენების პირობები',
    },
    specialists: {
      hero: {
        title: 'მეზღვაურებისთვის საზღვაო რეკრუტინგის მხარდაჭერა',
        desc: 'ეს განყოფილება ეძღვნება მეზღვაურებს, რომლებიც ეძებენ პროფესიულ მხარდაჭერას დასაქმებაში, პროფილის განხილვასა და კარიერულ შესაძლებლობებს გემთმფლობელებთან და ოპერატორებთან საზღვაო და ოფშორულ სექტორებში.',
        submitCV: 'მეზღვაურის CV-ს გაგზავნა',
        viewOpportunities: 'ვაკანსიების ნახვა',
        tagline1:
          'დაუკავშირდით გადამოწმებულ გემთმფლობელებსა და ოპერატორებს პროფესიული დასაქმების მხარდაჭერით.',
        tagline2:
          'გაგზავნეთ თქვენი პროფილი სტრუქტურირებული განხილვისთვის, დოკუმენტაციის კონსულტაციისა და შესაძლებლობების განხილვისთვის.',
      },
      seo: {
        title: 'ELVORA Consulting — მეზღვაურებისთვის რეკრუტინგის მხარდაჭერა',
        description:
          'პროფესიონალური რეკრუტინგის მხარდაჭერა მეზღვაურებისთვის: პროფილის შემოწმება, დოკუმენტაციასთან დახმარება და წვდომა აქტუალურ შესაძლებლობებზე გემთმფლობელებთან და ოპერატორებთან საზღვაო და ოფშორულ სექტორებში.',
      },
      benefits: {
        pill: 'მხარდაჭერა',
        title: 'პროფესიული დასაქმების\nმხარდაჭერა მეზღვაურებისთვის',
        desc1:
          'ჩვენ გთავაზობთ სტრუქტურირებულ მხარდაჭერას დასაქმებაში, პროფილის სკრინინგსა და დოკუმენტაციის კონსულტაციას მეზღვაურებისთვის, რომლებიც ეძებენ სამუშაოს გადამოწმებულ საზღვაო და ოფშორულ ოპერატორებთან.',
        desc2:
          'გაგზავნეთ თქვენი პროფილი სტრუქტურირებული განხილვისთვის, დოკუმენტაციის კონსულტაციისა და შესაძლებლობების განხილვისთვის.',
        items: {
          profile: {
            title: 'პროფილის განხილვა და სკრინინგი',
            desc: 'მეზღვაურთა პროფილები განიხილება რანგის, გემზე მუშაობის გამოცდილებისა და დოკუმენტაციის მზადყოფნის შესაფასებლად მიმდინარე ან მომავალი შესაძლებლობებისთვის.',
          },
          docs: {
            title: 'კონსულტაცია დოკუმენტაციაზე',
            desc: 'კონსულტაცია საჭირო სერტიფიკატებზე, დადასტურებებსა და დოკუმენტაციაზე, რომელსაც ითხოვენ გემთმფლობელები და ოფშორული ოპერატორები.',
          },
          matching: {
            title: 'მხარდაჭერა ვაკანსიების შერჩევაში',
            desc: 'პროფესიული მხარდაჭერა მეზღვაურთა პროფილების შესაბამისობაში მოყვანაში დასაქმების მოთხოვნებთან გამოცდილების, ხელმისაწვდომობისა და პროექტის მოთხოვნების საფუძველზე.',
          },
          access: {
            title: 'წვდომა გადამოწმებულ შესაძლებლობებზე',
            desc: 'განხილვა საზღვაო და ოფშორული შესაძლებლობებისთვის გემთმფლობელებთან და ოპერატორებთან პროფილის განხილვისა და კლიენტის მოთხოვნებთან შესაბამისობის პირობით.',
          },
        },
      },
      steps: {
        pill: 'პროცესი',
        title: 'როგორ მუშაობს',
        desc: 'თქვენი გზა განაცხადიდან დასაქმებამდე',
        items: [
          {
            title: 'პროფილის წარდგენა',
            desc: 'გაგზავნეთ თქვენი CV, რანგი, გემზე მუშაობის გამოცდილება და ხელმისაწვდომობა პროფესიული განხილვისა და განხილვისთვის.',
          },
          {
            title: 'განხილვა და სკრინინგი',
            desc: 'ჩვენ ვამოწმებთ თქვენს დოკუმენტებს და ვაფასებთ შესაბამისობას მიმდინარე და მომავალი დასაქმების მოთხოვნებისთვის.',
          },
          {
            title: 'განხილვა დასაქმებისთვის',
            desc: 'კვალიფიციური პროფილები შეირჩევა გემთმფლობელებისა და ოპერატორების მოთხოვნების შესაბამისად გამოცდილების საფუძველზე.',
          },
          {
            title: 'პირდაპირი ურთიერთობა კლიენტთან',
            desc: 'თუ თქვენი კანდიდატურა შეირჩა, თქვენ პირდაპირ უკავშირდებით დამსაქმებელ კომპანიას დასაქმების პირობების შესათანხმებლად.',
          },
        ],
      },
      requirements: {
        pill: 'დოკუმენტები',
        title: 'საჭირო დოკუმენტები დასაქმებისთვის',
        desc: 'სტანდარტული დოკუმენტები, რომლებიც ჩვეულებრივ მოითხოვება მეზღვაურის დაქირავებისა და პროფილის განხილვის პროცესში',
        items: [
          "მეზღვაურის პასპორტი (Seafarer's Book)",
          'საზღვარგარეთის პასპორტი (მოქმედი)',
          'კომპეტენციის სერტიფიკატი (CoC)',
          'STCW სერტიფიკატები',
          'სამედიცინო სერტიფიკატი (ENG1 / ეკვივალენტი)',
          'BOSIET / ოფშორული უსაფრთხოების სერტიფიკატები (თუ საჭიროა)',
          'DP სერტიფიკატი (DP პოზიციებისთვის, თუ საჭიროა)',
          'CV / ნაოსნობის სტაჟის წიგნაკი',
        ],
        note: 'საჭირო დოკუმენტების ზუსტი სია შეიძლება განსხვავდებოდეს გემის ტიპის, პროექტის მოთხოვნების, დროშის სახელმწიფოსა და კლიენტის პოლიტიკის მიხედვით.',
      },
      faq: {
        pill: 'FAQ',
        title: 'ხშირი კითხვები',
        items: [
          {
            q: 'იძლევით თუ არა დასაქმების გარანტიას CV-ს გაგზავნის შემდეგ?',
            a: 'არა. პროფილის გაგზავნა არ იძლევა დასაქმების გარანტიას. ყველა პროფილი განიხილება ინდივიდუალურად და შეიძლება გათვალისწინებული იყოს დასაქმების მოთხოვნებისთვის გამოცდილების, ხელმისაწვდომობისა და კლიენტის მოთხოვნების საფუძველზე.',
          },
          {
            q: 'მოქმედებთ როგორც საკრუინგო სააგენტო?',
            a: 'არა. Elvora გთავაზობთ დასაქმების მხარდაჭერასა და საკონსულტაციო მომსახურებას. დასაქმების გადაწყვეტილებები და კონტრაქტები ფორმდება უშუალოდ მეზღვაურებსა და დამსაქმებელ კომპანიებს შორის.',
          },
          {
            q: 'ახდევინებთ თუ არა საფასურს მეზღვაურებს?',
            a: 'ჩვენ არ ვახდევინებთ საფასურს მეზღვაურებს პროფილის გაგზავნისთვის. ზოგიერთი დამატებითი სერვისი, როგორიცაა კონსულტაცია დოკუმენტაციაზე ან კარიერული კონსალტინგი, შეიძლება შემოთავაზებული იყოს ცალკე მოთხოვნის შემთხვევაში.',
          },
          {
            q: 'რა ტიპის გემებსა და პროექტებზე მუშაობთ?',
            a: 'ჩვენ მხარს ვუჭერთ დასაქმების მოთხოვნებს საზღვაო და ოფშორულ სექტორებში, მათ შორის სავაჭრო გემებზე, ოფშორული ფლოტის გემებსა და საპროექტო ოპერაციებზე.',
          },
          {
            q: 'რამდენ ხანს გრძელდება განხილვის პროცესი?',
            a: 'პროფილის განხილვა დამოკიდებულია მიმდინარე დასაქმების აქტივობასა და კლიენტების მოთხოვნებზე. უკავშირდებიან მხოლოდ შერჩეულ კანდიდატებს.',
          },
          {
            q: 'როგორ დამიკავშირდებიან, თუ ჩემი პროფილი შესაფერისია?',
            a: 'შერჩეულ კანდიდატებს უკავშირდებიან პირდაპირ, პროფილის გაგზავნისას მითითებული საკონტაქტო მონაცემების გამოყენებით.',
          },
        ],
      },
      form: {
        pill: 'განაცხადი',
        title: 'მეზღვაურის პროფილის გაგზავნა',
        desc1:
          'გაგზავნეთ თქვენი პროფესიული პროფილი განსახილველად დასაქმების მხარდაჭერის პროცესის ფარგლებში.',
        desc2: 'გაგზავნა არ იძლევა დასაქმების გარანტიას.',
        labels: {
          firstName: 'სახელი',
          lastName: 'გვარი',
          rank: 'რანგი / პოზიცია',
          selectRank: 'აირჩიეთ რანგი/პოზიცია',
          specify: 'გთხოვთ დააზუსტოთ',
          enterPosition: 'შეიყვანეთ თქვენი პოზიცია',
          email: 'ელ. ფოსტა',
          phone: 'ტელეფონი / WhatsApp',
          phoneHint: 'მიუთითეთ ქვეყნის კოდი (მაგ., +1, +44, +995)',
          cv: 'CV-ს ატვირთვა (PDF, DOC ან სურათი)',
          dragDrop: 'გადმოიტანეთ თქვენი CV აქ ან დააჭირეთ ასარჩევად',
          fileTypes: 'PDF, DOC, DOCX, PNG, JPG, GIF, WebP (მაქს. 48 MB)',
          vesselType: 'გამოცდილება გემის ტიპის მიხედვით',
          selectVessel: 'აირჩიეთ გემის ტიპი',
          yearsExp: 'გამოცდილების წლები',
          availability: 'მზადყოფნის თარიღი',
          availabilityHint: 'აირჩიეთ მზადყოფნის უადრესი თარიღი',
          consent:
            'ვადასტურებ, რომ მოწოდებული ინფორმაცია ზუსტია და ვეთანხმები ჩემი პერსონალური მონაცემების დამუშავებას დასაქმების მხარდაჭერის მიზნით.',
          submit: 'პროფილის გაგზავნა',
          disclaimer:
            'ვინაიდან ინფორმაცია კონფიდენციალურია. უკავშირდებიან მხოლოდ შერჩეულ კანდიდატებს.',
        },
        ranks: {
          deck: 'გემბანის გუნდი',
          engine: 'სამანქანო გუნდი',
          other: 'სხვა',
          options: {
            master: 'კაპიტანი (Master)',
            chiefMate: 'უფროსი თანაშემწე (Chief Mate)',
            secondMate: 'მეორე თანაშემწე (Second Mate)',
            thirdMate: 'მესამე თანაშემწე (Third Mate)',
            bosun: 'ბოცმანი (Bosun)',
            ab: 'პირველი კლასის მატროსი (AB)',
            os: 'მეორე კლასის მატროსი (OS)',
            chiefEng: 'უფროსი მექანიკოსი (Chief Engineer)',
            secondEng: 'მეორე მექანიკოსი (Second Engineer)',
            thirdEng: 'მესამე მექანიკოსი (Third Engineer)',
            fourthEng: 'მეოთხე მექანიკოსი (Fourth Engineer)',
            eto: 'ელექტრომექანიკოსი (ETO)',
            asstEto: 'ელექტრომექანიკოსის ასისტენტი (Assistant ETO)',
            motorman: 'მოტორისტი (Motorman / Oiler)',
            wiper: 'ვაიპერი (Wiper)',
            dpo: 'DP ოპერატორი (DPO)',
            sdpo: 'უფროსი DP ოპერატორი (SDPO)',
            craneOp: 'ამწის ოპერატორი (Crane Operator)',
            rigger: 'სტროპალშიკი (Rigger)',
            electrician: 'ელექტრიკოსი (Electrician)',
            mechanic: 'მექანიკოსი (Mechanic)',
            other: 'სხვა (გთხოვთ დააზუსტოთ)',
          },
        },
        vessels: {
          tanker: 'ტანკერი',
          bulk: 'ბალკერი',
          container: 'კონტეინერმზიდი',
          offshore: 'ოფშორული ფლოტის გემი (OSV)',
          cruise: 'საკრუიზო გემი',
          other: 'სხვა',
        },
      },
      contact: {
        pill: 'კონტაქტი',
        title: 'გჭირდებათ დახმარება?',
        desc: 'დაგვიკავშირდით მესენჯერის ან ელ. ფოსტის საშუალებით სწრაფი დახმარებისთვის',
      },
    },
    companies: {
      hero: {
        title:
          'ეკიპაჟების საიმედო მიწოდება გემებისა და ოფშორული პროექტებისთვის',
        desc1:
          'პროფესიული რეკრუტინგი, სკრინინგი და ეკიპაჟის მართვა გემთმფლობელებისა და ოპერატორებისთვის.',
        desc2: 'ცალკეული პოზიციებიდან სრულ ეკიპაჟებამდე.',
        requestCrew: 'ეკიპაჟის მოთხოვნა',
        discussTask: 'დავალების განხილვა',
      },
      seo: {
        title:
          'ELVORA Consulting — ეკიპაჟის დაკომპლექტება გემებისა და ოფშორული პროექტებისთვის',
        description:
          'პროფესიონალური რეკრუტინგი, სკრინინგი და ეკიპაჟის მართვა გემთმფლობელებისა და ოპერატორებისთვის. ცალკეული პოზიციებიდან სრულ ეკიპაჟებამდე საზღვაო და ოფშორული პროექტებისთვის.',
      },
      services: {
        pill: 'სერვისები',
        title: 'სერვისები გემთმფლობელებისა\nდა ოპერატორებისთვის',
        desc1:
          'ეკიპაჟის შერჩევისა და მართვის კომპლექსური გადაწყვეტილებები საზღვაო და ოფშორული ოპერაციებისთვის.',
        desc2:
          'კანდიდატების შერჩევიდან მოთხოვნებთან სრულ შესაბამისობამდე და ეკიპაჟის ლოგისტიკამდე.',
        items: {
          recruitment: {
            title: 'ეკიპაჟის შერჩევა',
            desc: 'რეკრუტინგის სრული ციკლი ყველა რანგისა და გემის ტიპისთვის — ოფშორული და სავაჭრო ფლოტი.',
          },
          screening: {
            title: 'სკრინინგი და შემოწმება',
            desc: 'დოკუმენტების შემოწმება, ბიოგრაფიის, გამოცდილების დადასტურება და მოთხოვნებთან შესაბამისობის სკრინინგი.',
          },
          compliance: {
            title: 'შესაბამისობის მართვა',
            desc: 'STCW, MLC, დროშის სახელმწიფოსა და კლიენტის სპეციფიკური მოთხოვნების სრული დაფარვა.',
          },
          coordination: {
            title: 'ეკიპაჟის კოორდინაცია',
            desc: 'ეკიპაჟის ცვლილებები, მგზავრობის მორგება, მობილიზაცია და ჩანაცვლების დაგეგმვა.',
          },
        },
      },
      steps: {
        pill: 'პროცესი',
        title: 'როგორ ვმუშაობთ',
        desc: 'ეკიპაჟის შერჩევის მკაფიო და გამჭვირვალე პროცესი — მოთხოვნიდან გემზე ასვლამდე',
        items: [
          {
            title: 'მოთხოვნა',
            desc: 'გამოგვიგზავნეთ თქვენი მოთხოვნები ეკიპაჟზე და გემის ან პროექტის დეტალები',
          },
          {
            title: 'სკრინინგი',
            desc: 'კანდიდატების შერჩევა, დოკუმენტების შემოწმება და მოთხოვნებთან შესაბამისობა',
          },
          {
            title: 'შერჩევა და ინტერვიუ',
            desc: 'კვალიფიციური კანდიდატების მოკლე სიის ფორმირება და ინტერვიუს ორგანიზება',
          },
          {
            title: 'მობილიზაცია',
            desc: 'კონტრაქტის ხელმოწერა, ეკიპაჟის მობილიზაცია და მიმდინარე კოორდინაცია',
          },
        ],
      },
      compliance: {
        pill: 'შესაბამისობა',
        title: 'შესაბამისობა და კონფიდენციალურობა',
        desc: 'ჩვენ ვმუშაობთ საერთაშორისო საზღვაო სტანდარტების სრული დაცვით და უზრუნველყოფთ მონაცემთა მკაცრ დაცვას',
        items: {
          nda: {
            title: 'NDA და მონაცემთა დაცვა',
            desc: 'კლიენტებისა და ეკიპაჟის მთელი ინფორმაცია მუშავდება მკაცრი კონფიდენციალურობისა და გაუმჟღავნებლობის პრინციპების დაცვით.',
          },
          verification: {
            title: 'დოკუმენტების შემოწმება',
            desc: 'სერტიფიკატების, ლიცენზიების, სამედიცინო ცნობებისა და გამოცდილების საფუძვლიანი შემოწმება წარდგენამდე.',
          },
          stcw: {
            title: 'STCW და MLC შესაბამისობა',
            desc: 'სრული შესაბამისობა STCW, MLC 2006, დროშის სახელმწიფოსა და კლიენტის სპეციფიკურ მოთხოვნებთან.',
          },
        },
      },
      faq: {
        pill: 'FAQ',
        title: 'ხშირი კითხვები',
        items: [
          {
            q: 'მოქმედებთ როგორც დამსაქმებელი ან საკრუინგო სააგენტო?',
            a: 'არა. Elvora გთავაზობთ რეკრუტინგისა და ოპერაციული მხარდაჭერის სერვისებს. შრომითი ხელშეკრულებები ფორმდება უშუალოდ ეკიპაჟსა და დამსაქმებელ კომპანიას შორის.',
          },
          {
            q: 'რა ტიპის გემებსა და პროექტებს უჭერთ მხარს?',
            a: 'ჩვენ მხარს ვუჭერთ სავაჭრო გემებსა და ოფშორულ პროექტებს, მათ შორის ტანკერებს, ოფშორული ფლოტის გემებსა და საპროექტო საზღვაო ოპერაციებს.',
          },
          {
            q: 'უზრუნველყოფთ ეკიპაჟის ცალკეულ წევრებს თუ სრულ ეკიპაჟებს?',
            a: 'ჩვენ მხარს ვუჭერთ როგორც მოთხოვნებს ცალკეულ პოზიციებზე, ისე გადაწყვეტილებებს სრული ან ნაწილობრივი ეკიპაჟებისთვის, პროექტის მოცულობისა და კლიენტის მოთხოვნების მიხედვით.',
          },
          {
            q: 'როგორ უზრუნველყოფთ კანდიდატების ხარისხსა და მოთხოვნებთან შესაბამისობას?',
            a: 'ყველა კანდიდატი გადის დოკუმენტების შემოწმებას, გამოცდილების განხილვას და შემოწმებას STCW, MLC, დროშის სახელმწიფოსა და კლიენტის სტანდარტებთან შესაბამისობაზე.',
          },
          {
            q: 'რამდენად სწრაფად შეიძლება ეკიპაჟის მობილიზება?',
            a: 'მობილიზაციის ვადები დამოკიდებულია გემის ტიპზე, პოზიციაზე, ხელმისაწვდომობასა და დოკუმენტების სტატუსზე. ვადები განიხილება ინდივიდუალურად თითოეული მოთხოვნისთვის.',
          },
          {
            q: 'მუშაობთ მთელ მსოფლიოში?',
            a: 'დიახ. ჩვენ მხარს ვუჭერთ საერთაშორისო პროექტებს და ვთანამშრომლობთ გემთმფლობელებთან და ოპერატორებთან სხვადასხვა რეგიონში.',
          },
        ],
      },
      form: {
        pill: 'მოთხოვნა',
        title: 'ეკიპაჟის მოთხოვნა ან პროექტის განხილვა',
        desc1:
          'გამოგვიგზავნეთ თქვენი მოთხოვნები ეკიპაჟზე ან პროექტის დეტალები.',
        desc2:
          'ჩვენ განვიხილავთ თითოეულ მოთხოვნას ინდივიდუალურად და გიპასუხებთ.',
        sections: {
          company: 'ინფორმაცია კომპანიის შესახებ',
          details: 'მოთხოვნის დეტალები',
          timeline: 'ვადები',
        },
        labels: {
          companyName: 'კომპანიის სახელი',
          contactPerson: 'საკონტაქტო პირი',
          email: 'ელ. ფოსტა',
          phone: 'ტელეფონი / WhatsApp',
          vesselType: 'გემის / პროექტის ტიპი',
          selectVessel: 'აირჩიეთ გემის/პროექტის ტიპი',
          positions: 'საჭირო პოზიციები',
          selectPositions: 'აირჩიეთ პოზიციები',
          crewNumber: 'ეკიპაჟის რაოდენობა',
          location: 'ლოკაცია / რეგიონი',
          selectLocation: 'აირჩიეთ რეგიონი (არასავალდებულო)',
          additional: 'დამატებითი მოთხოვნები',
          additionalPlaceholder:
            'სპეციფიკური სერტიფიკატები, მოთხოვნები გამოცდილებაზე, პროექტის დეტალები...',
          startDate: 'სასურველი დაწყების თარიღი',
          duration: 'კონტრაქტის ხანგრძლივობა',
          selectDuration: 'აირჩიეთ ხანგრძლივობა (არასავალდებულო)',
          consent:
            'ვადასტურებ, რომ მოწოდებული ინფორმაცია ზუსტია და ვეთანხმები მონაცემთა დამუშავებას რეკრუტინგისა და ოპერაციული მხარდაჭერის მიზნით.',
          submit: 'მოთხოვნის გაგზავნა',
          disclaimer:
            'მთელი ინფორმაცია მუშავდება კონფიდენციალურად NDA-ს შესაბამისად. ჩვენ გიპასუხებთ 24 საათის განმავლობაში.',
        },
        vesselTypes: [
          'სავაჭრო გემი',
          'ტანკერი',
          'ოფშორული ფლოტის გემი (OSV)',
          'Jack-Up / ოფშორული დანადგარი',
          'საპროექტო ოპერაცია',
          'სხვა',
        ],
        regions: [
          'მთელი მსოფლიო',
          'ევროპა',
          'ახლო აღმოსავლეთი',
          'აფრიკა',
          'აზია-წყნარი ოკეანის რეგიონი',
          'სხვა',
        ],
        durations: {
          m1: '1 თვე',
          m2: '2 თვე',
          m3: '3 თვე',
          m4: '4 თვე',
          m6: '6 თვე',
          m9: '9 თვე',
          m12: '12 თვე',
          ongoing: 'მუდმივი / უვადო',
          project: 'საპროექტო',
        },
      },
      contact: {
        pill: 'კონტაქტი',
        title: 'დაგვიკავშირდით',
        desc: 'დაგვიკავშირდით ეკიპაჟის მოთხოვნებისთვის, კონსულტაციებისთვის ან პარტნიორობის შესაძლებლობებისთვის',
      },
    },
    formStatus: {
      sending: 'იგზავნება...',
      sent: 'გაგზავნილია ✅',
      error: 'შეცდომა, სცადეთ ხელახლა',
    },
    floating: {
      submitCV: 'CV-ს გაგზავნა',
      consultation: 'კონსულტაცია',
      consultTitle: 'დაგვიკავშირდით',
      consultDesc: 'დაგვიკავშირდით პირდაპირ ან გამოგვიგზავნეთ შეტყობინება',
      orSendMessage: 'ან გამოგვიგზავნეთ შეტყობინება',
      labelName: 'თქვენი სახელი',
      labelEmail: 'ელ. ფოსტა',
      labelPhone: 'ტელეფონი / WhatsApp',
      labelComment: 'კომენტარი',
      commentPlaceholder: 'როგორ შეგვიძლია დაგეხმაროთ?',
      submitBtn: 'შეტყობინების გაგზავნა',
      submitting: 'იგზავნება...',
      success: 'შეტყობინება გაიგზავნა!',
      successDesc: 'მალე დაგიკავშირდებით.',
      error: 'რაღაც შეცდომა მოხდა. სცადეთ ხელახლა.',
      disclaimer: 'მთელი ინფორმაცია კონფიდენციალურია.',
    },
  },
}
